import React, { useState, useEffect } from "react";
import { Box, TextField, MenuItem, Button, Grid, CircularProgress, Typography, IconButton, Divider, Stack } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useAddClinicalRecord, useUpdateClinicalRecord } from "store/hooks/react-query/query/useClinicalRecords";
import { toast } from "react-toastify";

const RECORD_TYPES = [
  { value: "VITAL_SIGNS", label: "Vital Signs Chart" },
  { value: "FLUID_BALANCE", label: "Fluid Balance Chart" },
  { value: "CLINICAL_NOTE", label: "Clinical Note (Dr/Nurse)" },
  { value: "MEDICATION", label: "Medication Administration" },
];

export default function ClinicalRecordForm({ patientId, initialData, onSuccess }) {
  const [recordType, setRecordType] = useState("VITAL_SIGNS");
  
  // --- NEW: Unified state for all standard text fields ---
  const [formValues, setFormValues] = useState({});
  
  // Dynamic state for medications
  const [medications, setMedications] = useState([{ medicationName: "", dose: "", frequency: "" }]);

  const { mutate: addRecord, isLoading: isAdding } = useAddClinicalRecord();
  const { mutate: updateRecord, isLoading: isUpdating } = useUpdateClinicalRecord();

  const isLoading = isAdding || isUpdating;

  // --- NEW: Pre-fill form when editing an existing record ---
  useEffect(() => {
    if (initialData) {
      setRecordType(initialData.recordType);
      
      if (initialData.recordType === 'MEDICATION' && initialData.details?.medications) {
        setMedications(initialData.details.medications);
      } else {
        setFormValues(initialData.details || {});
      }
    } else {
      // Reset if opening in "Add" mode
      setRecordType("VITAL_SIGNS");
      setFormValues({});
      setMedications([{ medicationName: "", dose: "", frequency: "" }]);
    }
  }, [initialData]);

  // --- NEW: Handle input changes for controlled standard fields ---
  const handleInputChange = (e) => {
    setFormValues({
      ...formValues,
      [e.target.name]: e.target.value
    });
  };

  const handleMedChange = (index, field, value) => {
    const updated = [...medications];
    updated[index][field] = value;
    setMedications(updated);
  };

  const addMedRow = () => setMedications([...medications, { medicationName: "", dose: "", frequency: "" }]);
  const removeMedRow = (index) => setMedications(medications.filter((_, i) => i !== index));

  const handleSubmit = (e) => {
    e.preventDefault();

    let payload = { patientId, recordType, ...formValues };

    // Format specific number fields
    if (recordType === 'FLUID_BALANCE') {
      payload.inputVolume = Number(formValues.inputVolume || 0);
      payload.outputVolume = Number(formValues.outputVolume || 0);

      delete payload.balance;
    }
    if (recordType === 'VITAL_SIGNS') {
      payload.temp = formValues.temp ? Number(formValues.temp) : undefined;
      payload.pulse = formValues.pulse ? Number(formValues.pulse) : undefined;
      payload.resp = formValues.resp ? Number(formValues.resp) : undefined;
      payload.spo2 = formValues.spo2 ? Number(formValues.spo2) : undefined;
      payload.rbs = formValues.rbs ? Number(formValues.rbs) : undefined;
    }
    if (recordType === 'MEDICATION') {
      // Filter out empty medication names
      const validMeds = medications.filter(m => m.medicationName.trim() !== "");
      if (validMeds.length === 0) return toast.error("Please enter at least one medication");
      payload = { patientId, recordType, medications: validMeds };
    }

    // --- UPDATED: Switch between POST and PUT ---
    if (initialData && initialData._id) {
      updateRecord(
        { id: initialData._id, data: payload }, 
        { 
          onSuccess: () => {
            toast.success("Record updated successfully");
            if (onSuccess) onSuccess(); 
          },
          onError: () => toast.error("Failed to update record")
        }
      );
    } else {
      addRecord(
        payload, 
        { 
          onSuccess: () => {
            toast.success("Record added successfully");
            if (onSuccess) onSuccess(); 
          },
          onError: () => toast.error("Failed to add record")
        }
      );
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
      <TextField
        select fullWidth label="Select Log Type"
        value={recordType}
        onChange={(e) => setRecordType(e.target.value)}
        sx={{ mb: 3 }}
        disabled={!!initialData} // Usually, we don't let users change the record TYPE during an edit
      >
        {RECORD_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
      </TextField>

      <Grid container spacing={2}>
        {/* --- VITAL SIGNS INPUTS --- */}
        {recordType === "VITAL_SIGNS" && (
          <>
            <Grid item xs={6}><TextField name="bp" label="BP (mmHg)" placeholder="120/80" value={formValues.bp || ""} onChange={handleInputChange} fullWidth required /></Grid>
            <Grid item xs={6}><TextField name="temp" label="Temp (°C)" type="number" inputProps={{ step: "0.1" }} value={formValues.temp || ""} onChange={handleInputChange} fullWidth required /></Grid>
            <Grid item xs={6}><TextField name="pulse" label="Pulse (bpm)" type="number" value={formValues.pulse || ""} onChange={handleInputChange} fullWidth required /></Grid>
            <Grid item xs={6}><TextField name="resp" label="Resp (cpm)" type="number" value={formValues.resp || ""} onChange={handleInputChange} fullWidth required /></Grid>
            <Grid item xs={6}><TextField name="spo2" label="SPO2 (%)" type="number" value={formValues.spo2 || ""} onChange={handleInputChange} fullWidth required /></Grid>
            <Grid item xs={6}><TextField name="rbs" label="RBS (mg/dL)" type="number" value={formValues.rbs || ""} onChange={handleInputChange} fullWidth required /></Grid>
          </>
        )}

        {/* --- MEDICATION INPUTS (DYNAMIC) --- */}
        {recordType === "MEDICATION" && (
          <Grid item xs={12}>
            {medications.map((med, index) => (
              <Box key={index} sx={{ mb: 2, p: 2, border: "1px dashed #ccc", borderRadius: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2">Medication {index + 1}</Typography>
                  {medications.length > 1 && (
                    <IconButton size="small" color="error" onClick={() => removeMedRow(index)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
                <Grid container spacing={1}>
                  <Grid item xs={12}><TextField label="Medication Name" required fullWidth size="small" value={med.medicationName} onChange={(e) => handleMedChange(index, "medicationName", e.target.value)} /></Grid>
                  <Grid item xs={6}><TextField label="Dosage (Optional)" fullWidth size="small" value={med.dose} onChange={(e) => handleMedChange(index, "dose", e.target.value)} /></Grid>
                  <Grid item xs={6}><TextField label="Freq (Optional)" fullWidth size="small" value={med.frequency} onChange={(e) => handleMedChange(index, "frequency", e.target.value)} /></Grid>
                </Grid>
              </Box>
            ))}
            <Button startIcon={<AddIcon />} onClick={addMedRow} variant="outlined" fullWidth sx={{ mt: 1 }}>
              Add Another Medication
            </Button>
          </Grid>
        )}

        {/* --- FLUID BALANCE INPUTS --- */}
        {recordType === "FLUID_BALANCE" && (
           <>
             <Grid item xs={6}><TextField name="inputType" label="Input (e.g. Saline)" value={formValues.inputType || ""} onChange={handleInputChange} fullWidth /></Grid>
             <Grid item xs={6}><TextField name="inputVolume" label="Vol (ml)" type="number" value={formValues.inputVolume || ""} onChange={handleInputChange} fullWidth /></Grid>
             <Grid item xs={6}><TextField name="outputType" label="Output (e.g. Urine)" value={formValues.outputType || ""} onChange={handleInputChange} fullWidth /></Grid>
             <Grid item xs={6}><TextField name="outputVolume" label="Vol (ml)" type="number" value={formValues.outputVolume || ""} onChange={handleInputChange} fullWidth /></Grid>
           </>
        )}

        {/* --- CLINICAL NOTE INPUTS --- */}
        {recordType === "CLINICAL_NOTE" && (
           <>
              <Grid item xs={12}><TextField name="authorName" label="Staff Name" value={formValues.authorName || ""} onChange={handleInputChange} required fullWidth /></Grid>
              <Grid item xs={12}>
                <TextField select name="role" label="Role" value={formValues.role || "NURSE"} onChange={handleInputChange} fullWidth>
                   <MenuItem value="DOCTOR">Doctor</MenuItem>
                   <MenuItem value="NURSE">Nurse</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}><TextField name="note" label="Observation/Report" multiline rows={4} value={formValues.note || ""} onChange={handleInputChange} required fullWidth /></Grid>
              <Grid item xs={12}>
                <TextField 
                  name="oxygenTherapy" 
                  label="Oxygen Therapy (Optional)" 
                  value={formValues.oxygenTherapy || ""} 
                  onChange={handleInputChange} 
                  fullWidth 
                />
              </Grid>
              <Grid item xs={12}>
                <TextField 
                  name="consumables" 
                  label="Consumables (Optional)" 
                  multiline 
                  rows={2} 
                  value={formValues.consumables || ""} 
                  onChange={handleInputChange} 
                  fullWidth 
                />
              </Grid>
           </>
        )}
      </Grid>

      <Button type="submit" variant="contained" fullWidth disabled={isLoading} sx={{ mt: 4 }}>
        {isLoading ? <CircularProgress size={24} color="inherit" /> : (initialData ? "Save Changes" : "Save Record")}
      </Button>
    </Box>
  );
}